/**
 * Reward Data Manager
 * 数据管理器：缓存数据，避免重复请求，管理数据更新事件
 */
(function() {
  'use strict';

  /**
   * 数据管理器（单例模式）
   */
  class RewardDataManager {
    constructor() {
      // 数据缓存
      this.cache = new Map();
      
      // 正在进行的请求（避免并发重复请求）
      this.pendingRequests = new Map();
      
      // 缓存过期时间（毫秒），默认5分钟
      this.cacheExpiry = 5 * 60 * 1000;
      
      // 事件名称常量
      this.EVENTS = {
        POINTS_UPDATED: 'reward:points:updated',
        DISCOUNTS_UPDATED: 'reward:discounts:updated',
        ACTIVITIES_UPDATED: 'reward:activities:updated',
        DATA_REFRESHED: 'reward:data:refreshed'
      };
    }

    /**
     * 生成缓存key
     */
    generateCacheKey(endpoint, params = {}) {
      const paramStr = Object.keys(params)
        .sort()
        .map(key => `${key}=${params[key]}`)
        .join('&');
      return `${endpoint}${paramStr ? '?' + paramStr : ''}`;
    }

    /**
     * 检查缓存是否有效
     */
    isCacheValid(cacheEntry) {
      if (!cacheEntry) return false;
      return Date.now() - cacheEntry.timestamp < this.cacheExpiry;
    }

    /**
     * 获取缓存数据
     */
    getCache(key) {
      const entry = this.cache.get(key);
      if (this.isCacheValid(entry)) {
        return entry.data;
      }
      // 缓存过期，删除
      if (entry) {
        this.cache.delete(key);
      }
      return null;
    }

    /**
     * 设置缓存
     */
    setCache(key, data) {
      this.cache.set(key, {
        data: data,
        timestamp: Date.now()
      });
    }

    /**
     * 清除缓存
     */
    clearCache(pattern = null) {
      if (!pattern) {
        // 清除所有缓存
        this.cache.clear();
      } else {
        // 清除匹配模式的缓存
        for (const key of this.cache.keys()) {
          if (key.includes(pattern)) {
            this.cache.delete(key);
          }
        }
      }
    }

    /**
     * 触发自定义事件
     */
    dispatchEvent(eventName, detail = {}) {
      const event = new CustomEvent(eventName, {
        detail: detail,
        bubbles: true,
        cancelable: true
      });
      window.dispatchEvent(event);
    }

    /**
     * 获取数据（带缓存和去重）
     * @param {string} endpoint - API端点
     * @param {object} params - 查询参数
     * @param {boolean} forceRefresh - 强制刷新，不使用缓存
     */
    async getData(endpoint, params = {}, forceRefresh = false) {
      const cacheKey = this.generateCacheKey(endpoint, params);

      // 如果强制刷新，清除缓存
      if (forceRefresh) {
        this.cache.delete(cacheKey);
      }

      // 检查缓存
      if (!forceRefresh) {
        const cachedData = this.getCache(cacheKey);
        if (cachedData !== null) {
          return cachedData;
        }
      }

      // 检查是否有正在进行的相同请求
      if (this.pendingRequests.has(cacheKey)) {
        // 等待正在进行的请求完成
        return await this.pendingRequests.get(cacheKey);
      }

      // 创建新的请求Promise
      const requestPromise = (async () => {
        try {
          if (!window.RewardApiService) {
            throw new Error('RewardApiService is not loaded');
          }

          const data = await window.RewardApiService.get(endpoint, params);
          
          // 缓存数据
          this.setCache(cacheKey, data);
          
          return data;
        } catch (error) {
          console.error('RewardDataManager getData error:', error);
          throw error;
        } finally {
          // 请求完成，移除pending状态
          this.pendingRequests.delete(cacheKey);
        }
      })();

      // 保存pending请求
      this.pendingRequests.set(cacheKey, requestPromise);

      return await requestPromise;
    }

    /**
     * 提交数据（POST/PUT/DELETE）
     * @param {string} method - HTTP方法
     * @param {string} endpoint - API端点
     * @param {object} data - 请求数据
     * @param {object} options - 额外选项
     */
    async submitData(method, endpoint, data = {}, options = {}) {
      try {
        if (!window.RewardApiService) {
          throw new Error('RewardApiService is not loaded');
        }

        // 埋点：业务操作开始
        this.trackBusinessEvent('business_action_start', {
          action: endpoint,
          method: method,
          data: data
        });

        let response;
        switch (method.toUpperCase()) {
          case 'POST':
            response = await window.RewardApiService.post(endpoint, data, options);
            break;
          case 'PUT':
            response = await window.RewardApiService.put(endpoint, data, options);
            break;
          case 'DELETE':
            response = await window.RewardApiService.delete(endpoint, data, options);
            break;
          default:
            throw new Error(`Unsupported method: ${method}`);
        }

        // 埋点：业务操作成功
        this.trackBusinessEvent('business_action_success', {
          action: endpoint,
          method: method,
          response: response
        });

        // 提交成功后，清除相关缓存并触发更新事件
        this.invalidateRelatedCache(endpoint);
        this.dispatchUpdateEvents(endpoint, response);

        return response;
      } catch (error) {
        // 埋点：业务操作失败
        this.trackBusinessEvent('business_action_error', {
          action: endpoint,
          method: method,
          error: error.message
        });
        
        console.error('RewardDataManager submitData error:', error);
        throw error;
      }
    }

    /**
     * 埋点：记录业务事件
     */
    trackBusinessEvent(eventName, eventData = {}) {
      const trackingData = {
        event: `reward_${eventName}`,
        timestamp: Date.now(),
        url: window.location.href,
        ...eventData
      };

      // Google Analytics / Google Tag Manager
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(trackingData);
      }

      // Facebook Pixel - 针对重要业务事件
      if (typeof window !== 'undefined' && window.fbq) {
        if (eventName === 'business_action_success') {
          const action = eventData.action || '';
          if (action.includes('/redeem')) {
            window.fbq('track', 'InitiateCheckout', {
              content_name: 'points_redeem',
              value: eventData.response?.points || eventData.data?.points || 0
            });
          }
        }
      }

      // Shopify Analytics
      if (typeof window !== 'undefined' && window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish('reward_business_event', trackingData);
      }
    }

    /**
     * 使相关缓存失效
     */
    invalidateRelatedCache(endpoint) {
      // 根据endpoint清除相关缓存
      // 例如：如果更新了积分，清除所有积分相关的缓存
      if (endpoint.includes('/points') || endpoint.includes('/balance')) {
        this.clearCache('/points');
        this.clearCache('/balance');
      }
      if (endpoint.includes('/discounts') || endpoint.includes('/redeem')) {
        this.clearCache('/discounts');
        this.clearCache('/redeem');
      }
      if (endpoint.includes('/activities')) {
        this.clearCache('/activities');
      }
    }

    /**
     * 触发更新事件
     */
    dispatchUpdateEvents(endpoint, responseData) {
      // 根据endpoint触发相应的事件
      if (endpoint.includes('/points') || endpoint.includes('/balance') || endpoint.includes('/redeem')) {
        this.dispatchEvent(this.EVENTS.POINTS_UPDATED, {
          data: responseData,
          endpoint: endpoint
        });
      }
      if (endpoint.includes('/discounts')) {
        this.dispatchEvent(this.EVENTS.DISCOUNTS_UPDATED, {
          data: responseData,
          endpoint: endpoint
        });
      }
      if (endpoint.includes('/activities')) {
        this.dispatchEvent(this.EVENTS.ACTIVITIES_UPDATED, {
          data: responseData,
          endpoint: endpoint
        });
      }
      
      // 触发通用刷新事件
      this.dispatchEvent(this.EVENTS.DATA_REFRESHED, {
        data: responseData,
        endpoint: endpoint
      });
    }

    /**
     * 刷新数据（强制重新获取）
     * @param {string} endpoint - API端点
     * @param {object} params - 查询参数
     */
    async refreshData(endpoint, params = {}) {
      return await this.getData(endpoint, params, true);
    }

    /**
     * 批量刷新多个数据源
     */
    async refreshMultiple(endpoints = []) {
      const promises = endpoints.map(({ endpoint, params }) => 
        this.refreshData(endpoint, params || {})
      );
      return await Promise.all(promises);
    }
  }

  // 导出单例实例
  window.RewardDataManager = new RewardDataManager();
})();

